import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import UIButton from "components/ui/form/UIButton";
import UISelect from "components/ui/select/UISelect";
import UITextField_V2 from "components/ui/textField/UITextFieldV2";
import specialityList from "../../../config/helpers/specialityData.json";
import { FormError } from "config/helpers/common_helper";
import { SAVE_USER_SETTINGS, UPDATE_PRACTITIONER } from "adapters/ehr.service";
import {
  set_current_user_practice_data,
  set_current_user_setting,
  set_practitioner_details,
} from "redux/actions/AuthActions";
import { toast } from "react-toastify";
import {
  AVAILABLE_LANGUAGES_FOR_SCRIBE,
  AVAILABLE_LANGUAGES_SOAP_NOTE,
} from "config/helpers/multilingual_helper";
import LanguageSelect from "components/common/LanguageSelect";
import { change_audio_language } from "redux/actions/AppActions";

const ehrOptions = [
  { label: "None", value: "None" },
  { label: "Epic Systems Corporation", value: "Epic Systems Corporation" },
  { label: "Oracle Cerner", value: "Oracle Cerner" },
  { label: "MEDITECH", value: "MEDITECH" },
  { label: "Evident a CPSI Company", value: "Evident a CPSI Company" },
  {
    label: "Altera Digital Health a Harris Company",
    value: "Altera Digital Health a Harris Company",
  },
  { label: "MEDHOST", value: "MEDHOST" },
  { label: "WellSky", value: "WellSky" },
  { label: "Vista", value: "Vista" },
  { label: "Netsmart Technologies", value: "Netsmart Technologies" },
  { label: "Proprietary Software", value: "Proprietary Software" },
  { label: "Other", value: "Other" },
];

const FirstVisit = ({ isOpen, handleClose }) => {
  const { i18n } = useTranslation();
  const theme = useSelector((state) => state?.app?.theme);
  const isMobile = useMediaQuery("(max-width:767px)");
  const initialValues = {
    speciality: "",
    specialityName: "",
    ehr: "",
    ehrValue: "",
  };
  const currentUserPracticeData = useSelector(
    (state) => state?.auth?.currentUserPracticeData
  );
  const reduxDispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const practitionerData = useSelector(
    (state) => state?.auth?.practitionerData
  );

  const currentUserSettingsData = useSelector(
    (state) => state?.auth?.currentUserSettings
  );
  const transformedSpecialityOptions = specialityList.map((option) => ({
    ...option,
    label: i18n.t(`SPECIALTY.${option.label}`),
  }));

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    watch,
    reset,
    clearErrors,
  } = useForm({
    defaultValues: initialValues,
  });
  const ehr = watch("ehr", "");
  const speciality = watch("speciality", "");
  const specialityName = watch("specialityName");
  const validationRules = {
    speciality: {
      required: i18n.t("SIGN_UP.SPECIALITY_REQUIRED"),
    },
    specialityName: {
      required: i18n.t("SIGN_UP.SPECIALITY_REQUIRED"),
    },
    ehr: {
      required: i18n.t("SIGN_UP.EHR_REQUIRED"),
    },
    ehrValue: {
      required: i18n.t("SIGN_UP.EHR_REQUIRED"),
    },
    speakingLanguage: {
      required: i18n.t("SIGN_UP.SPEAKING_LANGUAGE_REQUIRED"),
    },
  };

  const isCalendlyPopupOpen = () => {
    return document.querySelector(".calendly-popup") !== null;
  };

  const openCalendlyPopup = () => {
    if (!isCalendlyPopupOpen()) {
      window?.Calendly?.initPopupWidget({
        url: "https://cal.com/jaya-prakash-veerapaneni-v7yw8h/noki-demo?overlayCalendar=true",
      });
    } else {
      console.log("Calendly popup already open.");
    }
  };

  useEffect(() => {
    if (isOpen) {
      openCalendlyPopup();
    }
  }, [isOpen]);

  const updatePractitionerData = async (updateValue) => {
    const practitionerPayload = {
      ...practitionerData,
      qualification: [
        {
          code: {
            coding: [
              {
                code: updateValue?.practice_speciality?.value,
                display: updateValue?.practice_speciality?.label,
                system: "http://nucc.org/provider-taxonomy",
              },
            ],
            text: updateValue?.practice_speciality?.label,
          },
        },
      ],
    };

    try {
      const res = await UPDATE_PRACTITIONER("PUT", practitionerPayload);
      reduxDispatch(set_practitioner_details(res?.data));
    } catch (e) {
      console.error("Error updating practitioner data:", e);
    }
  };

  const updateUserSetting = async (updateValue, moduleName) => {
    debugger;
    let setting = {
      ...currentUserSettingsData?.display,
      ...updateValue,
    };
    console.log(
      "setting---",
      setting,
      "-currentUserSettingsData-",
      currentUserSettingsData,
      "-updateValue-",
      updateValue
    );

    let payload = structuredClone(currentUserSettingsData?.resource);
    payload.extension = [
      {
        url: "http://enliv.com/settings/data",
        valueString: JSON.stringify(setting),
      },
    ];
    try {
      const response = await SAVE_USER_SETTINGS(`PUT`, payload);
      const rawData = { ...response?.data };
      const displaySetting = rawData?.extension?.find(
        (item) => item?.url === "http://enliv.com/settings/data"
      )?.valueString;
      reduxDispatch(
        set_current_user_setting({
          resource: { ...rawData },
          display: JSON?.parse(displaySetting),
        })
      );
    } catch (e) {
      console.error(e);
    } finally {
      console.log("updated user settings");
    }
  };

  const updateProviderPracticeData = async (updateValue) => {
    let practice_data = {
      ...currentUserPracticeData?.practiceData,
      ...updateValue,
    };

    let payload = structuredClone(currentUserPracticeData?.resource);
    const newPracticeDataExtension = {
      url: "http://enliv.com/practice/data",
      valueString: JSON.stringify(practice_data),
    };

    if (!payload.extension) {
      payload.extension = [newPracticeDataExtension];
    } else {
      const existingExtensionIndex = payload.extension.findIndex(
        (ext) => ext.url === "http://enliv.com/practice/data"
      );

      if (existingExtensionIndex !== -1) {
        payload.extension.splice(
          existingExtensionIndex,
          1,
          newPracticeDataExtension
        );
      } else {
        payload.extension = [...payload.extension, newPracticeDataExtension];
      }
    }
    try {
      const response = await SAVE_USER_SETTINGS(`PUT`, payload);
      const rawData = { ...response?.data };
      const providerPracticeData = rawData?.extension?.find(
        (item) => item?.url === "http://enliv.com/practice/data"
      )?.valueString;
      reduxDispatch(
        set_current_user_practice_data({
          resource: { ...rawData },
          practiceData: JSON?.parse(providerPracticeData),
        })
      );
    } catch (e) {
      console.error(e);
    } finally {
      console.log("updated user settings");
    }
  };

  const onSubmit = async (data) => {
    const practice_data = {
      practice_speciality: {},
      practice_ehr: {},
      isFirstVisitSkipped: false,
    };

    const trimmedSpecialityName = data.specialityName.trim();

    const matchingSpeciality = specialityList.find(
      (option) =>
        option.label.toLowerCase() === trimmedSpecialityName.toLowerCase()
    );

    if (matchingSpeciality) {
      toast.info(i18n.t("error_messages.SPECIALITY_EXISTS"));
      return;
    }

    if (data.speciality === "Other") {
      practice_data.practice_speciality = {
        label: "Other",
        value: trimmedSpecialityName,
      };
    } else {
      const specialityMatch = specialityList.find(
        (option) => option.value === data.speciality
      );
      if (specialityMatch) {
        practice_data.practice_speciality = {
          label: specialityMatch.label,
          value: specialityMatch.value,
        };
      }
    }

    if (data.ehr === "Other") {
      practice_data.practice_ehr = {
        label: "Other",
        value: data.ehrValue,
      };
    } else {
      const ehrMatch = ehrOptions.find((option) => option.value === data.ehr);
      if (ehrMatch) {
        practice_data.practice_ehr = {
          label: ehrMatch.label,
          value: ehrMatch.value,
        };
      }
    }

    setIsSubmitting(true);

    try {
      await updateProviderPracticeData(practice_data);
      await updatePractitionerData(practice_data);
      await updateUserSetting({
        audioLanguage: watch("speakingLanguage")?.value || "en",
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }

    if (typeof handleClose === "function") {
      handleClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      sx={{
        ".MuiDialog-paper": {
          borderRadius: "24px",
        },
      }}
    >
      <Box
        sx={{
          borderRadius: "24px",
          border: "2px solid  rgba(154, 143, 143, 0.40)",
          padding:
            "min(60px,4.39vw)  min(2.92vw,40px) min(2.92vw,40px) min(2.92vw,40px)",
          minWidth: isMobile ? "" : "470px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1.75vw",
            margin: isMobile ? "0 1vh" : "0 30px",
          }}
        >
          <Typography
            sx={{
              fontSize: isMobile ? "1.45vh" : "min(1.75vw,23px)",
              fontWeight: 700,
              lineHeight: "33.6px",
            }}
          >
            {`${i18n.t("dashboard.LET_KNOW_ABOUT_PRACTICE")}`}
          </Typography>
          <Box
            width="100%"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Controller
              control={control}
              name="speciality"
              rules={validationRules.speciality}
              render={({ field }) => (
                <Box width={isMobile ? "75vw" : "100%"}>
                  <UISelect
                    onChange={(valueObj) => {
                      field?.onChange(valueObj?.value);
                      setValue("specialityName", "");
                      clearErrors("specialityName");
                    }}
                    options={transformedSpecialityOptions}
                    isSearchable
                    style={{
                      width: "95%",
                      height: "min(54px,3.95vw)",
                    }}
                    placeholder={
                      <Typography>{`${i18n.t(
                        "SIGN_UP.SELECT_SPECIALITY"
                      )}*`}</Typography>
                    }
                    menuPosition={"fixed"}
                    classNamePrefix="first-visit"
                    fullwidth
                  />
                  <FormError error={errors?.speciality?.message} />
                </Box>
              )}
            />
          </Box>

          {speciality === "Other" && (
            <Box
              width="100%"
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <Controller
                control={control}
                name="specialityName"
                rules={validationRules.specialityName}
                render={({ field }) => (
                  <Box width={isMobile ? "75vw" : "100%"}>
                    <TextField
                      {...field}
                      autoComplete="off"
                      autoFocus
                      placeholder={`${i18n.t("placeholder.ENTER_SPECIALITY")}*`}
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiInputBase-input::placeholder": {
                          opacity: 1,
                          color: theme === "dark" ? "#aaa" : "#aaa",
                        },
                        fontSize: isMobile ? "1.32vh" : "",
                      }}
                    />
                    <FormError error={errors?.specialityName?.message} />
                  </Box>
                )}
              />
            </Box>
          )}

          <Box
            width="100%"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Controller
              control={control}
              name="ehr"
              rules={validationRules.ehr}
              render={({ field }) => (
                <Box width={isMobile ? "75vw" : "100%"}>
                  <UISelect
                    fullwidth
                    onChange={(valueObj) => {
                      field?.onChange(valueObj?.value);
                      clearErrors("ehrValue");
                    }}
                    options={ehrOptions}
                    isSearchable
                    style={{
                      width: "95%",
                      height: "min(54px,3.95vw)",
                    }}
                    placeholder={
                      <Typography>{`${i18n.t(
                        "SIGN_UP.SELECT_EHR"
                      )}*`}</Typography>
                    }
                    menuPosition={"fixed"}
                    classNamePrefix="first-visit"
                  />
                  <FormError error={errors?.ehr?.message} />
                </Box>
              )}
            />
          </Box>
          {ehr === "Other" && (
            <Box
              width="100%"
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <Controller
                control={control}
                name="ehrValue"
                rules={validationRules.ehrValue}
                render={({ field }) => (
                  <Box width={isMobile ? "75vw" : "100%"}>
                    <TextField
                      {...field}
                      autoComplete="off"
                      autoFocus
                      placeholder={`${i18n.t("SIGN_UP.ENTER_EHR")}*`}
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiInputBase-input::placeholder": {
                          opacity: 1,
                          fontWeight: 500,
                          color: theme === "dark" ? "#aaa" : "#aaa",
                        },
                        fontSize: isMobile ? "1.32vh" : "",
                      }}
                    />
                    <FormError error={errors?.ehrValue?.message} />
                  </Box>
                )}
              />
            </Box>
          )}
          <Box
            width="100%"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Controller
              control={control}
              name="speakingLanguage"
              rules={{ required: i18n.t("SIGN_UP.SELECT_SPEAKING_LANGUAGE") }}
              render={({ field }) => (
                <Box width={isMobile ? "75vw" : "100%"}>
                  <UISelect
                    fullwidth
                    onChange={(e) => {
                      field?.onChange(e);
                      reduxDispatch(change_audio_language(e?.value));
                    }}
                    options={AVAILABLE_LANGUAGES_FOR_SCRIBE}
                    isSearchable
                    style={{
                      width: "95%",
                      height: "min(54px,3.95vw)",
                    }}
                    placeholder={
                      <Typography>{`${i18n.t(
                        "SIGN_UP.SELECT_SPEAKING_LANGUAGE"
                      )}*`}</Typography>
                    }
                    menuPosition="fixed"
                    classNamePrefix="first-visit"
                    getOptionLabel={(e) =>
                      e.isBilingual ? (
                        <div>
                          {e.label} ({i18n.t("common.ALSO_SUPPORTS_ENGLISH")})
                        </div>
                      ) : (
                        e.label
                      )
                    }
                    selectedValues={field.value}
                  />
                  <FormError error={errors?.speakingLanguage?.message} />
                </Box>
              )}
            />
          </Box>
          {/* <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          gap="1.95vw"
          mt={isMobile ? "min(5vh,5vw)" : "min(3vh,3vw)"}
        >
          <UIButton
            sx={{
              height: isMobile ? "40px" : "60px",
              width: isMobile ? "40vw" : "min(13vw,200px)",
              fontSize: isMobile ? "1.35vh" : "min(1.15vw,15px)",
            }}
            disabled={
              !isValid ||
              isSubmitting ||
              (speciality === "Other" && !watch("specialityName").trim())
            }
            onClick={handleSubmit(onSubmit)}
            isLoading={isSubmitting}
          >
            {`${i18n.t("SIGN_UP.SAVE")}`}
          </UIButton>
        </Box> */}

          <UIButton
            mt="20px"
            text={i18n.t("common.SAVE")}
            sx={{
              width: isMobile ? "75vw" : "100%",
              fontSize: isMobile ? "1.38vh" : "",
            }}
            callback={handleSubmit(onSubmit)}
            loading={isSubmitting}
            disabled={
              !isValid ||
              isSubmitting ||
              (speciality === "Other" && !watch("specialityName").trim())
            }
            loadingIndicator={
              <Typography
                sx={{
                  color: "#9E9E9E !important",
                  fontSize: "clamp(0.9vh, 0.9vw, 0.9vw)",
                }}
              >
                {i18n.t("common.SUBMITTING")}
              </Typography>
            }
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default FirstVisit;
